import { signOut } from 'firebase/auth';
import { firebaseAuth } from '../config/firebase';

export interface SessionUser {
  uid: string;
  email: string;
  name: string;
  avatarUrl?: string | null;
}

const DEV_USER_KEY = 'msb.devUser';
const ACCOUNTS_KEY = 'msb.accounts';

export interface RememberedAccount extends SessionUser {
  kind: 'dev' | 'google';
}

export function getRememberedAccounts(): RememberedAccount[] {
  try {
    const raw = window.localStorage.getItem(ACCOUNTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as RememberedAccount[];
    return parsed.filter((a) => a.uid && a.name);
  } catch {
    return [];
  }
}

export function rememberAccount(account: RememberedAccount): void {
  const rest = getRememberedAccounts().filter((a) => a.uid !== account.uid);
  window.localStorage.setItem(ACCOUNTS_KEY, JSON.stringify([account, ...rest].slice(0, 6)));
}

export function forgetAccount(uid: string): void {
  const rest = getRememberedAccounts().filter((a) => a.uid !== uid);
  window.localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(rest));
}

export function isDevHost(): boolean {
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
}

export function getDevUser(): SessionUser | null {
  try {
    const raw = window.localStorage.getItem(DEV_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SessionUser;
    if (!parsed.uid || !parsed.email) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setDevUser(user: SessionUser): void {
  window.localStorage.setItem(DEV_USER_KEY, JSON.stringify(user));
}

export function clearDevUser(): void {
  window.localStorage.removeItem(DEV_USER_KEY);
}

export async function getAuthHeaders(forceRefresh = false): Promise<Record<string, string>> {
  const dev = getDevUser();
  if (dev) {
    return {
      'X-Dev-User': dev.uid,
      'X-Dev-Email': dev.email,
      'X-Dev-Name': dev.name
    };
  }
  if (firebaseAuth?.currentUser) {
    const token = await firebaseAuth.currentUser.getIdToken(forceRefresh);
    return { Authorization: `Bearer ${token}` };
  }
  return {};
}

export function canRefreshToken(): boolean {
  return !getDevUser() && Boolean(firebaseAuth?.currentUser);
}

export async function endSession(): Promise<void> {
  clearDevUser();
  if (firebaseAuth) {
    await signOut(firebaseAuth);
  }
}
